import React, { FC } from "react";
import { connect } from "react-redux";

import { ICellRendererParams } from "@ag-grid-community/core";
import { faCheckCircle } from "@fortawesome/pro-solid-svg-icons/faCheckCircle";
import { faTimesCircle } from "@fortawesome/pro-solid-svg-icons/faTimesCircle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Popover from "antd/es/popover";
import { defineMessages, useIntl } from "react-intl";

import Chip from "@app/component/Chip";
import OrganizationSetting from "@app/enum/OrganizationSetting";
import { getOrganizationSettingValue } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";

import styles from "./ReliabilityCellRenderer.module.scss";

const messages = defineMessages({
  label: {
    id: "grid.reliabilityCell",
    defaultMessage: "{verified, select, true {Verified} other {Unverified}}",
    description: "(Un-)Verified label in Reliability Cell renderer",
  },
  verifiedCheckInDescription: {
    id: "grid.reliabilityCell.popover.description",
    defaultMessage:
      "A check-in visit is considered “Verified” if the assigned rep’s location was within {radius}m of the activity location provided",
    description: "A message for displaying verified checkin description",
  },
  verifiedCheckInTitle: {
    id: "grid.reliabilityCell.popover.title",
    defaultMessage: "Verified check-in",
    description: "A message for displaying verified checkin title",
  },
});

interface Props extends ICellRendererParams {
  checkInRadius: number;
}

const ReliabilityCellRenderer: FC<Props> = ({ checkInRadius, value }) => {
  const intl = useIntl();

  if (value === undefined || value === null) {
    // Wrapping into fragment because this component can't return a plain string.
    // On the other hand it can't return `null` because that's an AgGrid recommendation
    // to return empty strings: https://www.ag-grid.com/react-hooks/#react-null
    return <>{""}</>;
  }

  return (
    <div>
      <Popover
        content={
          <div className={styles.checkInDescription}>
            {intl.formatMessage(messages.verifiedCheckInDescription, { radius: checkInRadius })}
          </div>
        }
        placement="bottom"
        title={intl.formatMessage(messages.verifiedCheckInTitle)}
        trigger={["hover"]}
      >
        <Chip
          className={styles.container}
          icon={
            <FontAwesomeIcon
              className={value ? styles.verified : styles.unverified}
              icon={value ? faCheckCircle : faTimesCircle}
            />
          }
        >
          {intl.formatMessage(messages.label, { verified: value })}
        </Chip>
      </Popover>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  checkInRadius: getOrganizationSettingValue(state)(OrganizationSetting.CHECK_IN_RADIUS, 100),
});

export default connect(mapStateToProps, {})(ReliabilityCellRenderer);
